/**
 * Route Helper Utilities
 * Functions for creating and processing route configurations
 */

import React, { Suspense, lazy, ComponentType } from "react";
import { RouteObject, Navigate, useLocation } from "react-router-dom";
import { RouteConfig, RouteModule } from "./types";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { PageLoadingFallback } from "@/components/common/LoadingState";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

/**
 * Wraps a component with Suspense and ErrorBoundary for lazy loading
 */
export const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
  </ErrorBoundary>
);

/**
 * A redirect for a renamed/alias path that carries the router state along,
 * so the destination's guard still sees the after-sign-in flag (a refused
 * old link reached from sign-in goes home, not to Unauthorized).
 */
export const RedirectKeepingState: React.FC<{ to: string }> = ({ to }) => {
  const { state } = useLocation();
  return <Navigate to={to} replace state={state} />;
};

/**
 * Creates a lazy-loaded component from an import
 */
export function createLazyComponent<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T } | { [key: string]: T }>,
  exportName?: string
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().then((module) => {
      if (exportName && exportName in module) {
        return { default: (module as Record<string, T>)[exportName] };
      }
      return module as { default: T };
    })
  );
}

/**
 * Converts a RouteConfig to a React Router RouteObject
 */
export function configToRoute(config: RouteConfig): RouteObject {
  const { path, element, roles, permission, public: isPublic, withLayout = true } = config;

  // Public routes - no protection needed
  if (isPublic) {
    return { path, element };
  }

  // Protected routes
  let wrappedElement = element;

  // Wrap with layout if needed
  if (withLayout) {
    wrappedElement = <AppShell>{wrappedElement}</AppShell>;
  }

  // Wrap with protection
  if (roles || permission) {
    wrappedElement = (
      <ProtectedRoute roles={roles} permission={permission}>
        {wrappedElement}
      </ProtectedRoute>
    );
  } else {
    wrappedElement = <ProtectedRoute>{wrappedElement}</ProtectedRoute>;
  }

  return { path, element: wrappedElement };
}

/**
 * Processes a route module into RouteObjects
 */
export function processRouteModule(module: RouteModule): RouteObject[] {
  return module.routes.map((config) => {
    const fullPath = module.basePath
      ? `${module.basePath}${config.path}`
      : config.path;
    return configToRoute({ ...config, path: fullPath });
  });
}

/**
 * Combines multiple route modules into a single array
 */
export function combineRouteModules(...modules: RouteModule[]): RouteObject[] {
  return modules.flatMap(processRouteModule);
}

/**
 * Helper to wrap a page component with lazy loading
 */
export function lazyPage(element: React.ReactElement): React.ReactElement {
  return <LazyPage>{element}</LazyPage>;
}
